import {
    FunctionComponent,
    ReactElement,
    SyntheticEvent,
    useState,
} from "react";

import { Autocomplete, TextField } from "@mui/material";

import { useCreateRecipeTag, useGetRecipeTags } from "../../hooks";
import { IRecipeTag } from "../../sdk/types";

export interface IRecipeTagsSelectProps {
    value: string[];
    onChange: (tagIds: string[]) => void;
}

export const RecipeTagsSelect: FunctionComponent<IRecipeTagsSelectProps> = (
    props: IRecipeTagsSelectProps,
): ReactElement => {
    const { value, onChange } = props;
    const [inputValue, setInputValue] = useState("");

    const tagsQuery = useGetRecipeTags();
    const createTagMutation = useCreateRecipeTag();

    const tags = tagsQuery.data ?? [];
    const selectedTags = tags.filter((tag) => value.includes(tag.id));

    const handleChange = async (
        _event: SyntheticEvent,
        selected: (string | IRecipeTag)[],
    ): Promise<void> => {
        const resolved: string[] = [];

        for (const item of selected) {
            if (typeof item === "string") {
                const trimmed = item.trim();
                if (!trimmed) continue;

                const existing = tags.find(
                    (tag) => tag.name.toLowerCase() === trimmed.toLowerCase(),
                );

                if (existing) {
                    resolved.push(existing.id);
                } else {
                    const created = await createTagMutation.mutateAsync({
                        name: trimmed,
                    });
                    resolved.push(created.id);
                }
            } else {
                resolved.push(item.id);
            }
        }

        onChange([...new Set(resolved)]);
    };

    return (
        <Autocomplete
            multiple
            freeSolo
            size="small"
            fullWidth
            options={tags}
            value={selectedTags}
            getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name
            }
            isOptionEqualToValue={(option, selected) =>
                option.id ===
                (typeof selected === "string" ? selected : selected.id)
            }
            inputValue={inputValue}
            onInputChange={(_event, newInputValue) =>
                setInputValue(newInputValue)
            }
            onChange={handleChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add or create a tag"
                />
            )}
        />
    );
};
